import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, Server, Key, FileCheck, AlertTriangle, CheckCircle, Building, Mail, Globe } from "lucide-react";

export default function SecurityCompliance() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "March 2, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#121212] text-white py-6 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <span className="text-[#121212] font-bold text-xl">F</span>
            </div>
            <span className="text-2xl font-bold font-['Manrope']">FinAI</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms</Link>
            <Link to="/login" className="bg-[#BFFF00] text-[#121212] px-4 py-2 rounded-full font-medium hover:bg-[#a8e600]">Login</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#121212] to-[#1a1a1a] text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-[#121212]" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold font-['Manrope'] mb-4">Security & Compliance</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Enterprise-grade security protecting your financial data with industry-leading standards and certifications.
          </p>
          <p className="text-gray-500 mt-4">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Certifications Banner */}
      <div className="bg-[#F7FFE5] py-8 px-6 border-b border-[#BFFF00]/30">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm">
                <span className="text-sm font-bold text-[#121212]">SOC 2</span>
              </div>
              <p className="text-xs text-gray-600">Type II Aligned</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm">
                <span className="text-sm font-bold text-[#121212]">PCI DSS</span>
              </div>
              <p className="text-xs text-gray-600">Compliant</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm">
                <span className="text-sm font-bold text-[#121212]">ISO</span>
              </div>
              <p className="text-xs text-gray-600">27001 Aligned</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm">
                <span className="text-sm font-bold text-[#121212]">NIST</span>
              </div>
              <p className="text-xs text-gray-600">CSF Framework</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm">
                <span className="text-sm font-bold text-[#121212]">GLBA</span>
              </div>
              <p className="text-xs text-gray-600">Compliant</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Data Encryption */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#121212]" />
            </div>
            Data Encryption Policy
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-3">Encryption at Rest</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>AES-256 encryption</strong> for all stored data</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Encrypted database storage with AWS KMS</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Encrypted file storage for documents</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Hardware Security Modules (HSM) for key management</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-3">Encryption in Transit</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>TLS 1.3</strong> for all API communications</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>HTTPS enforced with HSTS preloading</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Certificate pinning for mobile applications</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Perfect Forward Secrecy (PFS) enabled</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Authentication & Access */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Key className="w-5 h-5 text-[#121212]" />
            </div>
            Authentication & Access Control
          </h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-[#121212] mb-3">Multi-Factor Authentication (MFA)</h3>
            <p className="text-gray-700 mb-4">
              We require and support multiple forms of authentication to ensure account security:
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span><strong>Email verification</strong> for account creation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span><strong>SMS/Phone verification</strong> for sensitive operations</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span><strong>Authenticator app support</strong> (TOTP-based)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span><strong>OAuth 2.0</strong> with bank-level security for Plaid connections</span>
              </li>
            </ul>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-3">Unique User IDs</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Cryptographically random user identifiers</li>
                <li>• UUID v4 format for all internal IDs</li>
                <li>• No predictable or sequential IDs</li>
                <li>• Session tokens with automatic expiration</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-3">Role-Based Access Control</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Granular permission systems</li>
                <li>• Principle of least privilege</li>
                <li>• Segregation of duties</li>
                <li>• Audit logging for all access</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Infrastructure Security */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Server className="w-5 h-5 text-[#121212]" />
            </div>
            Infrastructure Security
          </h2>
          
          <div className="bg-[#121212] text-white rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-[#BFFF00] mb-3">Cloud Infrastructure</h3>
            <p className="text-gray-300 mb-4">
              Our infrastructure is hosted on enterprise-grade cloud platforms with:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <ul className="text-sm text-gray-300 space-y-2">
                <li>• SOC 1, 2, and 3 certified data centers</li>
                <li>• Geographic redundancy (US-based)</li>
                <li>• 99.99% uptime SLA</li>
                <li>• Automated failover and disaster recovery</li>
              </ul>
              <ul className="text-sm text-gray-300 space-y-2">
                <li>• DDoS protection and mitigation</li>
                <li>• Web Application Firewall (WAF)</li>
                <li>• Intrusion Detection Systems (IDS)</li>
                <li>• 24/7 security monitoring</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Compliance Frameworks */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <FileCheck className="w-5 h-5 text-[#121212]" />
            </div>
            Compliance Frameworks
          </h2>

          <div className="space-y-6">
            <div className="border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-2">SOC 2 Type II Alignment</h3>
              <p className="text-gray-600 text-sm">
                Our security controls are designed and operated in alignment with the AICPA's SOC 2 framework, covering Security, Availability, Processing Integrity, Confidentiality, and Privacy trust service criteria.
              </p>
            </div>
            
            <div className="border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-2">PCI DSS Compliance</h3>
              <p className="text-gray-600 text-sm">
                All payment processing is handled through PCI DSS Level 1 compliant service providers (Stripe). We never store raw credit card numbers on our systems.
              </p>
            </div>
            
            <div className="border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-2">ISO 27001 Aligned Practices</h3>
              <p className="text-gray-600 text-sm">
                Our information security management system follows ISO 27001 best practices for risk management, security controls, and continuous improvement.
              </p>
            </div>
            
            <div className="border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-2">NIST Cybersecurity Framework</h3>
              <p className="text-gray-600 text-sm">
                We implement the NIST CSF's five core functions: Identify, Protect, Detect, Respond, and Recover to maintain a robust cybersecurity posture.
              </p>
            </div>
            
            <div className="border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-[#121212] mb-2">GLBA Safeguards Rule</h3>
              <p className="text-gray-600 text-sm">
                As a financial services provider, we maintain a comprehensive written information security program in compliance with the Gramm-Leach-Bliley Act Safeguards Rule.
              </p>
            </div>
          </div>
        </section>

        {/* Third-Party Providers */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#121212]" />
            </div>
            Third-Party Service Provider Policy
          </h2>
          
          <p className="text-gray-700 mb-6">
            We carefully vet all third-party service providers to ensure they meet our security standards:
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left font-semibold text-[#121212]">Provider</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#121212]">Purpose</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#121212]">Certifications</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 font-medium">Stripe</td>
                  <td className="px-4 py-3 text-gray-600">Payment Processing</td>
                  <td className="px-4 py-3 text-gray-600">PCI DSS Level 1, SOC 2</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Plaid</td>
                  <td className="px-4 py-3 text-gray-600">Bank Connections</td>
                  <td className="px-4 py-3 text-gray-600">SOC 2 Type II, ISO 27001</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">MongoDB Atlas</td>
                  <td className="px-4 py-3 text-gray-600">Database Storage</td>
                  <td className="px-4 py-3 text-gray-600">SOC 2, ISO 27001, HIPAA</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">AWS</td>
                  <td className="px-4 py-3 text-gray-600">Cloud Infrastructure</td>
                  <td className="px-4 py-3 text-gray-600">SOC 1/2/3, ISO 27001, PCI DSS</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Resend</td>
                  <td className="px-4 py-3 text-gray-600">Email Delivery</td>
                  <td className="px-4 py-3 text-gray-600">SOC 2 Type II</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Compliance Officer */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#121212]" />
            </div>
            Data Protection & Compliance Officer
          </h2>
          
          <div className="bg-[#F7FFE5] border border-[#BFFF00]/30 rounded-2xl p-6">
            <p className="text-gray-700 mb-4">
              Revurge Corporation has appointed a Chief Compliance Officer (CCO) who serves as our Data Protection Officer (DPO), responsible for:
            </p>
            <ul className="text-sm text-gray-600 space-y-2 mb-4">
              <li>• Overseeing data protection strategy and implementation</li>
              <li>• Ensuring compliance with applicable regulations</li>
              <li>• Handling data subject requests and inquiries</li>
              <li>• Managing security incident response</li>
              <li>• Conducting regular compliance audits</li>
            </ul>
            <div className="bg-white rounded-xl p-4">
              <p className="font-semibold text-[#121212]">Contact Compliance:</p>
              <p className="text-gray-600">Email: <a href="mailto:compliance@revurge.com" className="text-blue-600 hover:underline">compliance@revurge.com</a></p>
              <p className="text-gray-600 text-sm mt-2">Response time: Within 48 business hours</p>
            </div>
          </div>
        </section>

        {/* Incident Response */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            Security Incident Response
          </h2>
          
          <p className="text-gray-700 mb-4">
            Our incident response plan includes:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>24/7 security monitoring and alerting</li>
            <li>Documented incident classification and escalation procedures</li>
            <li>Customer notification within 72 hours of confirmed breach</li>
            <li>Root cause analysis and remediation</li>
            <li>Post-incident review and process improvement</li>
          </ul>
          
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
            <p className="text-sm text-red-800">
              <strong>Report a Security Issue:</strong> If you discover a potential security vulnerability, please report it immediately to <a href="mailto:security@revurge.com" className="underline">security@revurge.com</a>
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#121212]" />
            </div>
            Contact Information
          </h2>
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Building className="w-6 h-6 text-[#121212] mt-1" />
              <div>
                <p className="font-semibold text-[#121212]">Revurge Corporation</p>
                <p className="text-gray-600">8 The Green</p>
                <p className="text-gray-600">Dover, DE 19901</p>
                <p className="text-gray-600 mt-2">
                  <strong>General:</strong> <a href="mailto:info@revurge.com" className="text-blue-600 hover:underline">info@revurge.com</a>
                </p>
                <p className="text-gray-600">
                  <strong>Compliance:</strong> <a href="mailto:compliance@revurge.com" className="text-blue-600 hover:underline">compliance@revurge.com</a>
                </p>
                <p className="text-gray-600">
                  <strong>Security:</strong> <a href="mailto:security@revurge.com" className="text-blue-600 hover:underline">security@revurge.com</a>
                </p>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="bg-[#121212] text-white py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms of Service</Link>
            <Link to="/data-use" className="hover:text-[#BFFF00]">Data Use Policy</Link>
            <Link to="/cookies" className="hover:text-[#BFFF00]">Cookie Policy</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/faq" className="hover:text-[#BFFF00]">FAQ</Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">
            © {new Date().getFullYear()} Revurge Corporation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
